/** Plays the alert sounds of the catalog (soundCatalog.ts) and the published files of the server's
 * sounds folder (docs/alert_sounds.md). Browsers only allow sound after the person has done something
 * on the page; everything here stays quiet when audio is not allowed or not there.
 */

import { findSound, type Noise, type Recipe, type Tone } from "./soundCatalog";

export * from "./soundCatalog";

let context: AudioContext | null = null;
const decoded = new Map<string, Promise<AudioBuffer | null>>();

function audio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (Ctor === undefined) return null;
  context ??= new Ctor();
  if (context.state === "suspended") void context.resume();
  return context;
}

function playTone(
  ctx: AudioContext,
  [freq, start, length, wave, loud, glide]: Tone,
  at: number,
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = wave ?? "sine";
  osc.frequency.setValueAtTime(freq, at + start);
  if (glide !== undefined) osc.frequency.exponentialRampToValueAtTime(glide, at + start + length);
  gain.gain.setValueAtTime(0.0001, at + start);
  gain.gain.exponentialRampToValueAtTime(loud ?? 0.2, at + start + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + start + length);
  osc.connect(gain).connect(ctx.destination);
  osc.start(at + start);
  osc.stop(at + start + length + 0.03);
}

function playNoise(ctx: AudioContext, [start, length, hz, loud, kind]: Noise, at: number): void {
  const size = Math.max(1, Math.floor(ctx.sampleRate * length));
  const buffer = ctx.createBuffer(1, size, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let seed = 12345; // the same hiss every time: a plain generator is all a sound needs
  for (let i = 0; i < size; i += 1) {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    data[i] = seed / 2147483648 - 1;
  }
  const source = ctx.createBufferSource();
  const filter = ctx.createBiquadFilter();
  const gain = ctx.createGain();
  source.buffer = buffer;
  filter.type = kind;
  filter.frequency.value = hz;
  gain.gain.setValueAtTime(0.0001, at + start);
  gain.gain.exponentialRampToValueAtTime(loud, at + start + Math.min(0.02, length / 2));
  gain.gain.exponentialRampToValueAtTime(0.0001, at + start + length);
  source.connect(filter).connect(gain).connect(ctx.destination);
  source.start(at + start);
}

function playSynth(ctx: AudioContext, sound: Recipe): void {
  const at = ctx.currentTime;
  for (const tone of sound.tones ?? []) playTone(ctx, tone, at);
  for (const noise of sound.noise ?? []) playNoise(ctx, noise, at);
}

function say(text: string): void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(new SpeechSynthesisUtterance(text));
}

/** The id of a sound from the server's folder: "file:" and its path inside it. */
export const FILE_PREFIX = "file:";

/** Where a server sound is played from. */
export const soundFileUrl = (path: string): string =>
  `/api/sounds/file?id=${encodeURIComponent(path)}`;

/** A recording, decoded once; null when it cannot be had. */
function recording(ctx: AudioContext, url: string): Promise<AudioBuffer | null> {
  let found = decoded.get(url);
  if (found === undefined) {
    found = fetch(url, { credentials: "same-origin" })
      .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error("no file"))))
      .then((bytes) => ctx.decodeAudioData(bytes))
      .catch(() => null);
    decoded.set(url, found);
  }
  return found;
}

function playRecording(ctx: AudioContext, buffer: AudioBuffer): void {
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(ctx.destination);
  source.start();
}

/** Play a sound by id: one made in the browser, or `file:<path>` from the server's folder. Mute, an
 * unknown id and a browser without audio play nothing. */
export function playSound(id: string): void {
  if (id.startsWith(FILE_PREFIX)) {
    const ctx = audio();
    if (ctx !== null) {
      void recording(ctx, soundFileUrl(id.slice(FILE_PREFIX.length))).then((buffer) => {
        if (buffer !== null) playRecording(ctx, buffer);
      });
    }
    return;
  }
  const sound = findSound(id);
  if (sound === undefined) return;
  if (sound.speech !== undefined) {
    say(sound.speech);
    return;
  }
  const ctx = audio();
  if (ctx !== null) playSynth(ctx, sound);
}
